/* =========================================================================
 * THE NETWORK HALF of the field editor — the ONLY file here that imports
 * `lib/api.js`.
 *
 * The split is the same one `clearCrownRun.js` has beside `clearCrown.js`, and
 * for the same reason: `lib/api.js` reads `import.meta.env`, so anything
 * importing it cannot be driven under plain Node — and every RULE lives in
 * `fieldEdit.js` precisely so `verify-hunt.mjs` can execute it rather than
 * regex for it.
 *
 * Two calls, in this order, and the second is not optional:
 *   1. POST the one-field correction.
 *   2. Re-read the record DIRECTLY and compare.
 *
 * ⚠️ WHY STEP 2 EXISTS. `champion-update` has answered `ok: true` having changed
 * nothing, and `sr_segment` is an enum HubSpot can reject AFTER the route has
 * accepted the call. A UI that stops at step 1 tells the operator their
 * correction landed on evidence that does not support it — which is the exact
 * class of failure that let four wrong champions sit live for a day.
 * ========================================================================= */

import { api } from '../../../lib/api.js'
import { readEditResult, confirmWrite, editBefore, buildEditRequest } from './fieldEdit.js'

/**
 * Load the field policy. Returns the raw response for `readFieldPolicy()`.
 *
 * ⚠️ A FAILED POLICY READ MUST NOT DEGRADE OPEN. `readFieldPolicy` gives
 * `ok: false` for anything it cannot parse, and `mayEditField` then reports
 * every field as not drawable — so the editor disappears rather than drawing
 * inputs whose permissions nobody knows. The opposite default (assume editable,
 * let the server refuse) would draw a full set of live-looking controls that
 * 403 on every press.
 */
export async function loadFieldPolicy({ signal } = {}) {
  return api.championFieldPolicy({ signal })
}

/** The direct record read. Raw HubSpot properties — see the helper's note on
 *  why this is not the board snapshot. */
export async function readRecordRaw(id, { signal } = {}) {
  const res = await api.championshipRecord(id, { signal })
  return res?.record || null
}

/**
 * Apply one field correction and confirm it.
 *
 * Returns `{ verdict, confirmation }`. `verdict.kind === 'written'` is the only
 * success, and `confirmation.state` qualifies it — a `mismatch` means the route
 * said yes and the record disagrees, which the caller must render as NOT
 * corrected.
 */
export async function runFieldEdit({ record, field, value, reason, gate, lockProtected, allowClear = false }) {
  const id = String(record?.id || '')
  const body = buildEditRequest({ id, field, value, reason, gate, lockProtected, allowClear })

  let res
  try {
    res = await api.championFieldEdit(body, { before: editBefore(record, field) })
  } catch (err) {
    /* ⚠️ A REFUSAL ARRIVES AS A THROW, because `request()` throws ApiError on a
     * non-2xx — and both 403s (`data_repair_locked`, `automation_forbidden`) and
     * the 400 (`no_updatable_fields`) are non-2xx. So the parsed body is read
     * out of the error and handed to the SAME reader as a success. Treating a
     * throw as "network failure" would turn the server's specific, actionable
     * refusal — which names the grant and the exec who can set it — into
     * "something went wrong". */
    const body2 = err?.body
    if (body2 && typeof body2 === 'object') {
      return { verdict: readEditResult(body2, field), confirmation: null }
    }
    return {
      verdict: {
        kind: 'error',
        message: err?.message || 'The correction did not run.',
        hint: 'Nothing was written.',
      },
      confirmation: null,
    }
  }

  const verdict = readEditResult(res, field)
  if (verdict.kind !== 'written') return { verdict, confirmation: null }

  /* ⚠️ THE CONFIRMATION READ IS ITSELF ALLOWED TO FAIL WITHOUT UNDOING THE
   * VERDICT. The write is already done and cannot be rolled back, so a failed
   * re-read is reported as `unverified` — not as a failed correction. Calling a
   * completed write a failure would have somebody do it twice. */
  let afterRaw = null
  try {
    afterRaw = await readRecordRaw(id)
  } catch {
    afterRaw = null
  }

  return { verdict, confirmation: confirmWrite({ field, expected: value, afterRaw }), afterRaw, recordId: id }
}
